import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  updateDoc,
  writeBatch
} from "firebase/firestore";
import { Field, FieldArray, Formik, useField } from "formik";
import { CheckCircle2, FolderPlus, ListChecks, Plus } from "lucide-react";
import { CSSProperties, useEffect, useId, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { InputField } from "../forms/InputField";
import { Question } from "../hooks/useVisaQuestions";
import { RowDragHandleCell } from "../components/common/DndHelpers";
import { VISAVALUE } from "../types/visa.type";

const FIELD_TYPE_OPTIONS: { value: Question["fieldType"]; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio" },
  { value: "textarea", label: "Textarea" },
  { value: "select", label: "Select" },
  { value: "period", label: "Period" },
  { value: "wage", label: "Wage" }
];

const ReadOnlyField = ({
  label,
  name,
  placeholder
}: {
  label: string;
  name: string;
  placeholder?: string;
}) => {
  const id = useId();
  const [field] = useField<string>(name);
  return (
    <div className="flex w-full flex-col gap-1.5">
      <Label htmlFor={id} className="font-normal text-fg-2">
        {label}
      </Label>
      <Input
        {...field}
        id={id}
        value={field.value ?? ""}
        placeholder={placeholder}
        readOnly
      />
    </div>
  );
};

const SortableItem = ({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) => {
  const { attributes, setNodeRef, transform } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform)
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full" {...attributes}>
      {children}
    </div>
  );
};

const DroppableZone = ({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) => {
  const { active, setNodeRef, transform } = useSortable({
    id,
    data: {
      type: "section-zone"
    }
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform)
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "h-auto w-full rounded-xl bg-elevated ring-1",
        active ? "ring-accent-line" : "ring-line-2"
      )}
    >
      {children}
    </div>
  );
};

export const VisaQuestionnaireBuilder = () => {
  const { visa } = useParams();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [sections, setSections] = useState<Record<string, Question[]>>({});
  const [selectedVisa, setSelectedVisa] = useState<string>(visa || "");
  const db = getFirestore();
  const visaSelectId = useId();

  const EXCLUDED_VISAS: VISAVALUE[] = [VISAVALUE.EMPTY, VISAVALUE.EB2NIW];
  const displayVisas = useMemo(
    () =>
      Object.values(VISAVALUE).filter(
        (v) => v !== "" && !EXCLUDED_VISAS.includes(v as VISAVALUE)
      ) as string[],
    []
  );

  useEffect(() => {
    if (!selectedVisa) return;

    const fetchQuestions = async () => {
      const questionsRef = collection(
        db,
        `visa_questions/${selectedVisa}/questions`
      );
      const q = query(questionsRef, orderBy("index")); // Sort by index

      const querySnapshot = await getDocs(q);
      const fetchedQuestions: Question[] = querySnapshot.docs.map<Question>(
        (doc) => ({
          ...(doc.data() as Question),
          id: doc.id
        })
      );
      const sectionsTemp: Record<string, Question[]> = {};

      fetchedQuestions.forEach((question) => {
        const { section } = question;
        const sectionDefault =
          !section || section === "" ? "Unassigned" : section;
        if (!sectionDefault) return;
        if (!sectionsTemp[sectionDefault]) {
          sectionsTemp[sectionDefault] = [question];
        } else {
          sectionsTemp[sectionDefault].push(question);
        }
      });
      setSections(sectionsTemp);
    };

    fetchQuestions();
  }, [selectedVisa, db]);

  const handleUpdateOrder = async (): Promise<void> => {
    const batch = writeBatch(db);

    const allQuestions = Object.values(sections).flatMap((val) => val);

    try {
      for (const [index, question] of allQuestions.entries()) {
        const questionRef = doc(
          db,
          `visa_questions/${selectedVisa}/questions/${question.id}`
        );
        // eslint-disable-next-line object-shorthand
        batch.update(questionRef, { index: index });
      }

      await batch.commit();
    } catch (error) {
      toast.error("Oops, something went wrong", {
        description:
          "There was an error when updating the order. Please try again. If the problem persists, please contact support.",
        duration: 5000
      });
    }
  };

  const handleSectionRename = async (
    oldName: string,
    newName: string
  ): Promise<void> => {
    if (oldName === newName) return;

    const batch = writeBatch(db);
    const updatedSections = { ...sections };

    updatedSections[newName] = updatedSections[oldName] || [];
    delete updatedSections[oldName];

    updatedSections[newName].forEach((question) => {
      const questionRef = doc(
        db,
        `visa_questions/${selectedVisa}/questions/${question.id}`
      );
      batch.update(questionRef, { section: newName });
    });

    try {
      await batch.commit();
      setSections(updatedSections);
    } catch (error) {
      Sentry.captureException(error);
    }
  };
  const [activeId, setActiveId] = useState<
    DragStartEvent["active"]["id"] | null
  >(null);
  // Add a new question
  const addQuestion = async (section?: string) => {
    const newQuestion: Question = {
      id: "", // Firestore will generate this
      fieldType: "text",
      index: questions.length + 1,
      isRequired: false,
      questionText: `question_${questions.length + 1}`,
      variableName: `question_${questions.length + 1}`,
      description: "Add a description here",
      options: [],
      tooltipText: "",
      section: section === "Unassigned" ? "" : section,
      subVariableName: null,
      subIndex: null,
      subOptions: null,
      subQuestions: null
    };

    try {
      const questionsRef = collection(
        db,
        `visa_questions/${selectedVisa}/questions`
      );
      const docRef = await addDoc(questionsRef, newQuestion);
      const questionWithId = { ...newQuestion, id: docRef.id };
      if (!sections.Unassigned) {
        setSections((sections) => ({
          ...sections,
          Unassigned: [questionWithId]
        }));
      } else if (newQuestion.section) {
        setSections((sections) => ({
          ...sections,
          [newQuestion.section!]: [
            ...sections[newQuestion.section!],
            questionWithId
          ]
        }));
        setQuestions([...questions, questionWithId]);
      } else {
        setSections((sections) => ({
          ...sections,
          Unassigned: [...sections.Unassigned, questionWithId]
        }));
      }
      setQuestions([...questions, questionWithId]);
    } catch (error) {
      Sentry.captureException(error);
    }
  };
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    setActiveId(active.id);
  };
  const addSection = () => {
    setSections((sections) => ({
      ...sections,
      [`Section${Object.keys(sections).length + 1}`]: []
    }));
  };

  // Delete a question
  const deleteQuestion = async (questionId: string, sectionName: string) => {
    try {
      const updatedQuestions = sections[sectionName].filter(
        (question) => question.id !== questionId
      );
      const questionRef = doc(
        db,
        `visa_questions/${selectedVisa}/questions/${questionId}`
      );
      await deleteDoc(questionRef);

      setSections((sections) => ({
        ...sections,
        [sectionName]: updatedQuestions
      }));
      // setQuestions(questions.filter((_, i) => _.id !== questionId));
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Save changes for a single question
  const saveQuestion = async (updatedQuestion: Question) => {
    try {
      const questionRef = doc(
        db,
        `visa_questions/${selectedVisa}/questions/${updatedQuestion.id}`
      );
      await updateDoc(questionRef, { ...updatedQuestion });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Handle drag-and-drop sorting
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  function moveKeyBefore(
    obj: Record<string, any>,
    keyToMove: string,
    beforeKey: string
  ): Record<string, any> {
    const entries = Object.entries(obj);
    const result: [string, any][] = [];

    for (const [key, value] of entries) {
      if (key === beforeKey) {
        if (keyToMove in obj) {
          result.push([keyToMove, obj[keyToMove]]);
        }
      }
      if (key !== keyToMove) {
        result.push([key, value]);
      }
    }

    return Object.fromEntries(result);
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // shuffling sections
    if (
      active.data.current &&
      over.data.current &&
      over.data.current.type === "section-zone" &&
      active.data.current.type === "section-zone"
    ) {
      const activeIndex = Object.keys(sections).indexOf(activeId.toString());
      const overIndex = Object.keys(sections).indexOf(overId.toString());

      const copyOfSections = { ...sections };
      if (activeIndex < overIndex) {
        const updatedSections = moveKeyBefore(
          copyOfSections,
          overId.toString(),
          activeId.toString()
        );
        setSections(updatedSections);
      } else {
        const updatedSections = moveKeyBefore(
          copyOfSections,
          activeId.toString(),
          overId.toString()
        );
        setSections(updatedSections);
      }
    }

    // If shuffling questions within a section
    if (activeId !== overId) {
      const sectionName = Object.keys(sections).find((section) =>
        sections[section].some((q) => q.id === activeId)
      );

      if (sectionName) {
        const updatedQuestions = arrayMove(
          sections[sectionName],
          sections[sectionName].findIndex((q) => q.id === activeId),
          sections[sectionName].findIndex((q) => q.id === overId)
        );

        setSections((prevSections) => ({
          ...prevSections,
          [sectionName]: updatedQuestions
        }));
      }
    }
  };

  useEffect(() => {
    handleUpdateOrder();
  }, [sections]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Visa Questionnaire Builder"
          actions={
            selectedVisa ? (
              <>
                <Button type="button" size="sm" onClick={addSection}>
                  <FolderPlus />
                  Add Section
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => addQuestion()}
                >
                  <Plus />
                  Add Question
                </Button>
              </>
            ) : undefined
          }
        />
        <div className="flex items-center gap-2">
          <Label htmlFor={visaSelectId} className="font-normal text-fg-2">
            Select Visa Type
          </Label>
          <Select
            value={selectedVisa || undefined}
            onValueChange={setSelectedVisa}
          >
            <SelectTrigger id={visaSelectId} className="w-60">
              <SelectValue placeholder="Choose a visa type..." />
            </SelectTrigger>
            <SelectContent>
              {displayVisas.map((visaType) => (
                <SelectItem key={visaType} value={visaType}>
                  {visaType.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-24">
        {selectedVisa ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <SortableContext
              items={[...Object.keys(sections)]}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex max-w-240 flex-col gap-4">
                {/* Sections */}
                {Object.entries(sections).map(([sectionName, questions]) => (
                  <DroppableZone key={sectionName} id={sectionName}>
                    <div className="flex items-center gap-2 border-b border-line-1 p-2">
                      <RowDragHandleCell rowId={sectionName} />
                      <Input
                        aria-label="Section Name"
                        defaultValue={sectionName}
                        className="flex-1 font-medium"
                        onBlur={(e) =>
                          handleSectionRename(sectionName, e.target.value)
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Confirm Section Name"
                      >
                        <CheckCircle2 />
                      </Button>
                    </div>
                    <SortableContext
                      items={questions.map((q) => q.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Accordion
                        type="multiple"
                        className="divide-y divide-line-1"
                      >
                        {questions.map((question, index) => (
                          <SortableItem key={question.id} id={question.id}>
                            <div className="flex w-full grow items-start gap-1 py-1 pr-3 pl-2">
                              <RowDragHandleCell rowId={question.id} />
                              <AccordionItem
                                value={question.id}
                                className="min-w-0 flex-1 border-0"
                              >
                                <AccordionTrigger className="py-1.5 text-base font-normal text-fg hover:no-underline">
                                  {question.questionText ||
                                    `Question ${index + 1}`}
                                </AccordionTrigger>
                                <AccordionContent className="pt-2 pb-3">
                                  <Formik
                                    initialValues={question}
                                    enableReinitialize // Ensure form updates with the latest question data
                                    onSubmit={(values) => saveQuestion(values)}
                                  >
                                    {({
                                      values,
                                      handleSubmit,
                                      setFieldValue
                                    }) => (
                                      <form
                                        onSubmit={handleSubmit}
                                        className="flex flex-col gap-3 text-base"
                                      >
                                        <InputField
                                          label="Question Label"
                                          name="questionText"
                                          placeholder="Question Text"
                                        />
                                        <InputField
                                          label="ToolTip Text"
                                          name="tooltipText"
                                          placeholder="ToolTip Text"
                                        />
                                        <ReadOnlyField
                                          label="Variable Name"
                                          name="variableName"
                                          placeholder="Variable Name"
                                        />
                                        <InputField
                                          label="Description"
                                          name="description"
                                          placeholder="Description"
                                        />
                                        <div className="flex flex-col gap-1.5">
                                          <Label
                                            htmlFor={`${question.id}-fieldType`}
                                            className="font-normal text-fg-2"
                                          >
                                            Field Type
                                          </Label>
                                          <Select
                                            name="fieldType"
                                            value={
                                              values.fieldType || undefined
                                            }
                                            onValueChange={(value) =>
                                              setFieldValue("fieldType", value)
                                            }
                                          >
                                            <SelectTrigger
                                              id={`${question.id}-fieldType`}
                                              className="w-full"
                                            >
                                              <SelectValue placeholder="Select" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {FIELD_TYPE_OPTIONS.map(
                                                (option) => (
                                                  <SelectItem
                                                    key={option.value}
                                                    value={option.value}
                                                  >
                                                    {option.label}
                                                  </SelectItem>
                                                )
                                              )}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        {values.fieldType === "radio" ||
                                        values.fieldType === "checkbox" ||
                                        values.fieldType === "select" ? (
                                          <FieldArray
                                            name="options"
                                            render={(optionHelpers) => (
                                              <div className="flex flex-col gap-2 rounded-md bg-fill-1 p-3">
                                                <span className="text-sm font-medium text-fg-2">
                                                  Options
                                                </span>
                                                {values.options?.map(
                                                  (option, optIndex) => (
                                                    <div
                                                      key={optIndex}
                                                      className="flex items-center gap-2"
                                                    >
                                                      <Field
                                                        name={`options[${optIndex}]`}
                                                        as={Input}
                                                        placeholder="Option"
                                                      />
                                                      <Button
                                                        type="button"
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() =>
                                                          optionHelpers.remove(
                                                            optIndex
                                                          )
                                                        }
                                                      >
                                                        Delete
                                                      </Button>
                                                    </div>
                                                  )
                                                )}
                                                <Input
                                                  placeholder="Paste multiple options separated by commas"
                                                  onBlur={(e) => {
                                                    const pastedOptions =
                                                      e.target.value
                                                        .split(",")
                                                        .map((opt) =>
                                                          opt.trim()
                                                        )
                                                        .filter(
                                                          (opt) =>
                                                            opt.length > 0
                                                        );
                                                    pastedOptions.forEach(
                                                      (opt) =>
                                                        optionHelpers.push(opt)
                                                    );
                                                    e.target.value = ""; // Clear after adding
                                                  }}
                                                />
                                                <div>
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    onClick={() =>
                                                      optionHelpers.push(
                                                        "New Option"
                                                      )
                                                    }
                                                  >
                                                    <Plus />
                                                    Add Single Option
                                                  </Button>
                                                </div>
                                              </div>
                                            )}
                                          />
                                        ) : null}

                                        <Label className="flex items-center gap-2 text-base text-fg font-normal leading-normal">
                                          <Switch
                                            id={`${question.id}-isRequired`}
                                            name="isRequired"
                                            checked={Boolean(values.isRequired)}
                                            onCheckedChange={(checked) =>
                                              setFieldValue(
                                                "isRequired",
                                                checked
                                              )
                                            }
                                          />
                                          Required Field
                                        </Label>
                                        <Label className="flex items-center gap-2 text-base text-fg font-normal leading-normal">
                                          <Switch
                                            id={`${question.id}-allowDuplicates`}
                                            name="allowDuplicates"
                                            checked={Boolean(
                                              values.allowDuplicates
                                            )}
                                            onCheckedChange={(checked) =>
                                              setFieldValue(
                                                "allowDuplicates",
                                                checked
                                              )
                                            }
                                          />
                                          Allow Duplicates
                                        </Label>
                                        <div className="flex justify-end gap-2 pt-2">
                                          <Button
                                            type="button"
                                            variant="danger"
                                            size="sm"
                                            onClick={() =>
                                              deleteQuestion(
                                                question.id,
                                                sectionName
                                              )
                                            }
                                          >
                                            Delete Question
                                          </Button>
                                          <Button
                                            type="submit"
                                            variant="primary"
                                            size="sm"
                                          >
                                            Save Question
                                          </Button>
                                        </div>
                                      </form>
                                    )}
                                  </Formik>
                                </AccordionContent>
                              </AccordionItem>
                            </div>
                          </SortableItem>
                        ))}
                      </Accordion>
                      <div className="border-t border-line-1 p-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addQuestion(sectionName)}
                        >
                          <Plus />
                          Add Question
                        </Button>
                      </div>
                    </SortableContext>
                  </DroppableZone>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyState
            icon={ListChecks}
            title="No Visa Type Selected"
            description="Please select a visa type to edit questions."
          />
        )}
      </div>
    </div>
  );
};
